// @ts-strict-ignore
import React, { useContext } from 'react';
import AggregationSelect from './AggregationSelect';
import {
  StyledAggregationSection,
  StyledAggregationCriteriaRow,
  StyledAggregationClause,
  StyledTimeframeNumberInput,
} from './styles';
import { AggregationEditorContext } from './context';
import { getIsPreview } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/helpers';
import { ThresholdContext } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/context';
import { getMultiSelectProps } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/helpers';
import { RuleBuilderContext } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/context';
import { useAppSelector } from '~/store/reduxHooks';
import { selectRuleBuilderActiveSubEditor } from 'flow/store/redux/selectors/ui';

import {
  AGGREGATION_OPTIONS,
  SCOPE_OPTIONS,
  SUBJECT_OPTIONS,
  TIMEFRAME_UNIT_OPTIONS,
} from './constants';
import type { AggregationSelectFieldKey, TimeframeUnit } from './types';
import type { AggregationOption } from './AggregationSelect';

const BaseAggregationsSection = () => {
  const { sourceData, setSourceData } = useContext(AggregationEditorContext);
  const { editMode, containerType } = useContext(RuleBuilderContext);
  const { populatedThreshold } = useContext(ThresholdContext);
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);

  const multiSelectProps = getMultiSelectProps({
    action: populatedThreshold?.left?.action,
    options: {
      isPreview: populatedThreshold
        ? getIsPreview(editMode, populatedThreshold, activeSubEditor.thresholdId)
        : false,
    },
  });

  const handleSelectChange = (field: AggregationSelectFieldKey) => (option: AggregationOption) => {
    const { value } = option;
    switch (field) {
      case 'timeframeUnit':
        setSourceData(prev => ({ ...prev, [field]: value as TimeframeUnit }));
        break;
      case 'aggregation':
      case 'subject':
      case 'scope':
        setSourceData(prev => ({ ...prev, [field]: value }));
        break;
    }
  };

  const handleTimeframeNumberChange = (field: 'timeframeIntervalStart' | 'timeframeIntervalEnd') => (newValue: number) => {
    setSourceData(prev => ({ ...prev, [field]: newValue }));
  };

  return (
    <StyledAggregationSection>
      <StyledAggregationCriteriaRow>
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={AGGREGATION_OPTIONS}
          value={sourceData.aggregation}
          onChange={handleSelectChange('aggregation')}
        />
        <StyledAggregationClause containerType={containerType}>
          of
        </StyledAggregationClause>
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={SUBJECT_OPTIONS}
          value={sourceData.subject}
          onChange={handleSelectChange('subject')}
        />
        <StyledAggregationClause containerType={containerType}>
          with the following criteria:
        </StyledAggregationClause>
      </StyledAggregationCriteriaRow>

      <StyledAggregationCriteriaRow>
        <StyledAggregationClause containerType={containerType}>
          Scope:
        </StyledAggregationClause>
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={SCOPE_OPTIONS}
          value={sourceData.scope}
          onChange={handleSelectChange('scope')}
        />
      </StyledAggregationCriteriaRow>

      <StyledAggregationCriteriaRow>
        <StyledAggregationClause containerType={containerType}>
          Timeframe:
        </StyledAggregationClause>
        <StyledTimeframeNumberInput
          minValue={0}
          maxValue={999}
          value={sourceData.timeframeIntervalStart}
          onHandleValueChange={handleTimeframeNumberChange('timeframeIntervalStart')}
        />
        <StyledAggregationClause containerType={containerType}>
          to
        </StyledAggregationClause>
        <StyledTimeframeNumberInput
          minValue={sourceData.timeframeIntervalStart}
          // TODO: This should be dynamic based on the attribute/units (e.g. days, weeks, months, years)
          maxValue={999}
          value={sourceData.timeframeIntervalEnd}
          onHandleValueChange={handleTimeframeNumberChange('timeframeIntervalEnd')}
        />
        <AggregationSelect
          multiSelectProps={multiSelectProps}
          options={TIMEFRAME_UNIT_OPTIONS}
          value={sourceData.timeframeUnit}
          onChange={handleSelectChange('timeframeUnit')}
        />
        <StyledAggregationClause containerType={containerType}>
          ago
        </StyledAggregationClause>
      </StyledAggregationCriteriaRow>
    </StyledAggregationSection>
  );
};

export default BaseAggregationsSection;
